import React, { useEffect, useRef, useState } from 'react';
import { FaClipboardList, FaExclamationCircle } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import BottomNav from "../../../components/BottomNav";
import HeaderWithBack from '../../../components/HeaderWithBack';
import { useTeleconsultationDisclaimer } from '../../../hooks/useTeleconsultationDisclaimer';
import { useAuthStore } from '../../../stores/authStores';
import { useCoverage } from '../../../hooks/useCoverage';
import { usePayment } from "../../../hooks/usePayment";
import DoctorSwitchModal, { DoctorOption } from '../../../components/modal/DoctorSwitchModal';
import PendingPaymentModal from '../../../components/modal/PendingPaymentModal';
import InfoModal from '../../../components/modal/InfoModal';
import { cancelPendingPayment, PendingPayment } from '../../../services/pendingPaymentService';
import { useHome } from '../../../hooks/useHome';
import { activeConsultationMessage, activeConsultationRoute, isActiveConsultation } from '../../../utils/activeConsultation';

const Disclaimer: React.FC = () => {
  const navigate = useNavigate();
  const { practitionerRoleId } = useParams<{ practitionerRoleId: string }>();
  const isAuto = practitionerRoleId === 'auto';
  const userData = useAuthStore((state) => state.user);
  const authToken = useAuthStore((state) => state.token);
  const { data: coverage, isLoading: coverageLoading } = useCoverage(authToken);
  const coverageInfo = coverage?.data;

  const { creatingRequest, createConsultationRequest } = usePayment({ authToken, navigate });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string | undefined>(isAuto ? undefined : practitionerRoleId);
  const [unavailableNotice, setUnavailableNotice] = useState<string | null>(null);
  const [pendingPayment, setPendingPayment] = useState<PendingPayment | null>(null);
  const [cancellingPending, setCancellingPending] = useState(false);
  const [infoModal, setInfoModal] = useState<{
    title: string;
    message: string;
    actionLabel?: string;
    goHome?: boolean;
  } | null>(null);

  const coverageText = () => {
    if (coverageLoading) return null;

    if (!coverageInfo || !coverageInfo.is_covered) {
      return "Telekonsultasi tetap tersedia, namun belum termasuk dalam manfaat asuransi. Biaya layanan menjadi tanggungan pribadi.";
    }
    return null;
  };
  const text = coverageText();

  const { pertanyaan, updateField, save } = useTeleconsultationDisclaimer();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    updateField(name, value);
  };

  const submitRequest = async (roleId?: string) => {
    if (isSubmitting) return;

    if (!pertanyaan.berat || !pertanyaan.tinggi) {
      setInfoModal({
        title: 'Data Belum Lengkap',
        message: 'Mohon isi berat dan tinggi badan sebelum melanjutkan konsultasi.',
      });
      return;
    }

    setIsSubmitting(true);
    save();

    try {
      const result = await createConsultationRequest({
        user_id: userData.id,
        practitioner_role_id: isAuto ? null : roleId,
        doctor_assign_status: isAuto ? "unassigned" : "assigned",
        pertanyaan,
      });

      const tcId = result.teleconsultId || result.teleconsult_id || '';
      const encTk = result.encToken || result.enc_token || '';

      if (tcId) {
        localStorage.setItem(`teleconsult_token_${tcId}`, encTk);
      }

      navigate(`/connect-doctor/${tcId}/${encTk}?stage=${result.stage === 'final-confirm' ? 'final-confirm' : 'initial'}`, {
        replace: true,
        state: { practitionerRoleId: roleId },
      });
    } catch (err: any) {
      const data = err.response?.data;

      if (!isAuto && (data?.code === 'DOCTOR_BUSY' || data?.code === 'DOCTOR_OFFLINE')) {
        setUnavailableNotice(data.message || 'Dokter sedang tidak tersedia');
        return;
      }

      if (data?.code === 'PENDING_PAYMENT_EXISTS' && data.pending_payment) {
        setPendingPayment(data.pending_payment);
        return;
      }

      if (err.response?.status === 409 && data?.teleconsultId) {
        const activeId = data.teleconsultId;
        const activeToken = data.encToken || '';
        localStorage.setItem(`teleconsult_token_${activeId}`, activeToken);

        if (data.stage === 'payment') {
          navigate(`/checkout-teleconsultation/${activeId}`, { replace: true });
        } else if (data.stage === 'chat') {
          navigate(`/chat/${activeId}/${activeToken}`, { replace: true });
        } else if (data.stage === 'initial' || data.stage === 'final-confirm') {
          navigate(`/connect-doctor/${activeId}/${activeToken}?stage=${data.stage}`, { replace: true });
        } else {
          setInfoModal({
            title: 'Konsultasi Masih Berjalan',
            message: data.message || 'Anda masih memiliki konsultasi yang sedang berjalan.',
            actionLabel: 'Kembali ke Beranda',
            goHome: true,
          });
        }
        return;
      }

      const alreadyConsulted = /sudah konsultasi hari ini/i.test(data?.message || '');
      setInfoModal(alreadyConsulted
        ? {
            title: 'Konsultasi Hari Ini Sudah Digunakan',
            message: 'Anda sudah melakukan konsultasi hari ini. Konsultasi dapat dilakukan kembali besok.',
            actionLabel: 'Kembali ke Beranda',
            goHome: true,
          }
        : {
            title: 'Gagal Membuat Permintaan',
            message: data?.message || 'Gagal membuat permintaan konsultasi. Silakan coba lagi.',
          });
    } finally {
      setIsSubmitting(false);
    }
  };

  const { data: homeData, refetch: refetchHome } = useHome(userData?.id, authToken);
  const activeConsultation = isActiveConsultation(homeData?.teleconsult) ? homeData.teleconsult : null;
  const [showActiveConsultation, setShowActiveConsultation] = useState(false);
  const activeCheckedRef = useRef(false);

  useEffect(() => {
    if (activeCheckedRef.current || !authToken) return;
    activeCheckedRef.current = true;
    refetchHome().then((result) => {
      if (isActiveConsultation(result.data?.teleconsult)) setShowActiveConsultation(true);
    });
  }, [authToken, refetchHome]);

  const handleContinue = async () => {
    if (isSubmitting) return;
    const fresh = (await refetchHome()).data ?? homeData;
    if (isActiveConsultation(fresh?.teleconsult)) {
      setShowActiveConsultation(true);
      return;
    }
    submitRequest(selectedRoleId);
  };

  const continuePendingPayment = () => {
    if (!pendingPayment) return;
    const pending = pendingPayment;
    setPendingPayment(null);

    if (pending.payment_url) {
      navigate(`/payment-success?teleconsult_id=${pending.teleconsult_id}&enc_token=${pending.enc_token || ''}`, { replace: true });
      return;
    }
    navigate(`/checkout-teleconsultation/${pending.teleconsult_id}`, { replace: true });
  };

  const cancelPendingAndRetry = async () => {
    if (!pendingPayment || !authToken) return;
    setCancellingPending(true);
    try {
      await cancelPendingPayment(pendingPayment.teleconsult_id, authToken);
      setPendingPayment(null);
      await submitRequest(selectedRoleId);
    } catch (err: any) {
      setInfoModal({
        title: 'Gagal Membatalkan',
        message: err.response?.data?.message || 'Gagal membatalkan pembayaran. Silakan coba lagi.',
      });
    } finally {
      setCancellingPending(false);
    }
  };

  const handleSwitchDoctor = (doctor: DoctorOption) => {
    const roleId = String(doctor.id);
    setUnavailableNotice(null);
    setSelectedRoleId(roleId);
    submitRequest(roleId);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <HeaderWithBack title="Disclaimer" />
      {text && (
        <div className="mt-3 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-800">
          {text}
        </div>
      )}

      {/* Profile */}
      <div className="flex justify-center mt-4">
        <div className="bg-white p-4 rounded-xl shadow-md text-center w-11/12 max-w-md">
          <img
            src={userData?.person.avatar_url || `https://m.linksehat.com/assets/img/users/${userData?.person.gender}-avatar.png`}
            alt="Avatar"
            className="w-16 h-16 rounded-full mx-auto mb-2"
          />
          <p className="text-teal-700 font-semibold">{userData?.person.name}</p>
        </div>
      </div>

      {/* Pertanyaan */}
      <div className="flex justify-center mt-4">
        <div className="bg-white p-4 rounded-xl shadow-md w-11/12 max-w-md">
          <div className="flex items-center mb-2">
            <FaClipboardList className="text-teal-500 mr-2" />
            <h2 className="text-teal-600 font-bold text-lg">Pertanyaan</h2>
          </div>
          <p className="mb-4">Silahkan jawab Pertanyaan berikut ini:</p>

          {/* Input Berat */}
          <div className="mb-4">
            <label className="font-medium">
              Berapa berat badan Anda? <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              placeholder="Masukkan berat badan (kg)"
              className="w-full mt-1 p-2 border rounded"
              name="berat"
              value={pertanyaan.berat}
              onChange={handleInputChange}
            />
          </div>

          {/* Input Tinggi */}
          <div className="mb-4">
            <label className="font-medium">
              Berapa tinggi badan Anda? <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              placeholder="Masukkan tinggi badan (cm)"
              className="w-full mt-1 p-2 border rounded"
              name='tinggi'
              value={pertanyaan.tinggi}
              onChange={handleInputChange}
            />
          </div>

          {/* Radio: Sakit lebih 3 hari */}
          <div className="mb-4">
            <label className="font-medium">Apakah sakit sudah lebih dari 3 hari?</label>
            <div className="flex gap-4 mt-1">
              <label>
                <input 
                  type="radio" 
                  name="lamaSakit" 
                  onChange={handleInputChange} 
                  value="1"
                  checked={pertanyaan.lamaSakit === '1'}
                /> Ya
              </label>
              <label>
                <input 
                  type="radio" 
                  name="lamaSakit" 
                  onChange={handleInputChange} 
                  value="0"
                  checked={pertanyaan.lamaSakit === '0'}
                /> Tidak
              </label>
            </div>
          </div>

          {/* Radio: Sudah minum obat */}
          <div className="mb-4">
            <label className="font-medium">Apakah sudah minum obat?</label>
            <div className="flex gap-4 mt-1">
              <label>
                <input 
                  type="radio" 
                  name="minumObat" 
                  onChange={handleInputChange} 
                  value="1"
                  checked={pertanyaan.minumObat === '1'}
                /> Ya
              </label>
              <label>
                <input 
                  type="radio" 
                  name="minumObat" 
                  onChange={handleInputChange} 
                  value="0"
                  checked={pertanyaan.minumObat === '0'}
                /> Tidak
              </label>
            </div>
          </div>

          {/* Radio: Riwayat Covid */}
          <div className="mb-4">
            <label className="font-medium">Apakah anda memiliki riwayat covid?</label>
            <div className="flex gap-4 mt-1">
              <label>
                <input 
                  type="radio" 
                  name="riwayatCovid" 
                  onChange={handleInputChange} 
                  value="1"
                  checked={pertanyaan.riwayatCovid === '1'}
                /> Ya
              </label>
              <label>
                <input 
                  type="radio" 
                  name="riwayatCovid" 
                  onChange={handleInputChange} 
                  value="0"
                  checked={pertanyaan.riwayatCovid === '0'}
                /> Tidak
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer Box */}
      <div className="flex justify-center mt-4 mb-4 px-4">
        <div className="w-full md:w-7/12 bg-white text-gray-800 rounded-lg shadow-lg border border-gray-200">
          <div className="p-3">
            <h3 className="text-2xl font-semibold flex items-center gap-2">
              <FaExclamationCircle className="text-teal-500" />
              Disclaimer
            </h3>
          </div>
          <hr className="w-full border-gray-200" />
          <div className="p-3 text-sm">
            <p>
              Fasilitas ini belum menjamin kepastian diagnosa kondisi yang dialami.
              Perbincangan dalam aplikasi tidak bisa menggantikan interaksi fisik dengan dokter.
              Untuk kondisi darurat, segera temui dokter langsung.
            </p>
          </div>
        </div>
      </div>

      {/* Tombol Cancel dan Continue */}
      <div className="flex w-full justify-between mt-4 mb-28 px-4">
        <button
          onClick={() => navigate('/home')}
          className="px-6 py-2 text-gray-800 uppercase bg-white rounded-full shadow-lg"
        >
          Cancel
        </button>
        <button
          onClick={handleContinue}
          disabled={isSubmitting}
          className={`px-6 py-2 text-white uppercase rounded-full shadow-lg ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'bg-teal-500'}`}
        >
          {isSubmitting ? 'Memproses...' : 'Continue'}
        </button>
      </div>

      <InfoModal
        show={showActiveConsultation && !!activeConsultation}
        title="Konsultasi Masih Berjalan"
        message={activeConsultation ? activeConsultationMessage(activeConsultation) : ''}
        actionLabel="Lihat Konsultasi"
        secondaryLabel="Kembali ke Beranda"
        onSecondary={() => {
          setShowActiveConsultation(false);
          navigate('/home', { replace: true });
        }}
        onClose={() => {
          setShowActiveConsultation(false);
          if (activeConsultation) navigate(activeConsultationRoute(activeConsultation), { replace: true });
        }}
      />

      <InfoModal
        show={!!infoModal}
        title={infoModal?.title || ''}
        message={infoModal?.message || ''}
        actionLabel={infoModal?.actionLabel}
        onClose={() => {
          const goHome = infoModal?.goHome;
          setInfoModal(null);
          if (goHome) navigate('/home', { replace: true });
        }}
      />

      <PendingPaymentModal
        pending={pendingPayment}
        onContinue={continuePendingPayment}
        onCancelAndNew={cancelPendingAndRetry}
        onClose={() => setPendingPayment(null)}
        loading={cancellingPending}
      />

      <DoctorSwitchModal
        isOpen={!!unavailableNotice}
        token={authToken as string}
        excludeRoleId={selectedRoleId}
        title={unavailableNotice || 'Dokter sedang tidak tersedia'}
        description="Slot dokter yang Anda pilih sedang penuh. Pilih dokter lain yang tersedia untuk langsung melanjutkan konsultasi."
        stayLabel="Kembali"
        showPrice
        confirmMessage={(doctor) => (
          <>
            Permintaan konsultasi akan dikirim ke{' '}
            <span className="font-semibold text-gray-700">{doctor.doctorName}</span>.
            <br />
            Biaya konsultasi mengikuti tarif dokter ini.
          </>
        )}
        onClose={() => setUnavailableNotice(null)}
        onConfirm={handleSwitchDoctor}
      />

      <BottomNav />
    </div>
  );
};

export default Disclaimer;